import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCartIcon, HeartIcon, ShareIcon, CheckIcon, StarIcon, TagIcon, CalendarIcon, UserIcon, EyeIcon, PlayIcon, InfoIcon, PencilIcon, LayoutDashboardIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { VideoPlayer } from '../components/video/VideoPlayer';
import { ResponsiveVideoPlayer } from '../components/video/ResponsiveVideoPlayer';
import { useProduct } from '../hooks/useFirestore';
import { useAuth } from '../components/auth/AuthProvider';
import { useCart } from '../components/cart/CartContext';
import { useFavorites } from '../hooks/useFavorites';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { motion } from 'framer-motion';
// Move helper function outside of component
const getVideoPlayerStyles = (isVertical = false) => {
  if (isVertical) {
    return {
      maxWidth: '100%',
      margin: '0 auto'
    };
  }
  return {
    width: '100%',
    height: '100%'
  };
};
export const ProductDetailPage = () => {
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const {
    product,
    loading,
    error
  } = useProduct(productId);
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    addItem,
    openCart
  } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isFavorited,
    toggleFavorite
  } = useFavorites();
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState<'idle' | 'adding' | 'added' | 'error'>('idle');
  const [sellerStoreOnline, setSellerStoreOnline] = useState<boolean | null>(null);
  const [sellerCheckLoading, setSellerCheckLoading] = useState(true);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const [pendingAddToCart, setPendingAddToCart] = useState(false);
  // Set the selected image once the product is loaded
  useEffect(() => {
    if (product && product.image) {
      setSelectedImage(product.image);
    }
  }, [product]);
  // Handle click outside to close share menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareOptions(false);
      }
    };
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowShareOptions(false);
      }
    };
    if (showShareOptions) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showShareOptions]);
  // Fetch seller's storeOnline status
  useEffect(() => {
    if (!product?.sellerId) {
      setSellerCheckLoading(false);
      return;
    }
    // Skip check if the current user is the owner
    if (user?.id && user.id === product.sellerId) {
      setSellerStoreOnline(true);
      setSellerCheckLoading(false);
      return;
    }
    const checkSellerStore = async () => {
      try {
        const sellerDoc = await getDoc(doc(db, 'users', product.sellerId!));
        if (sellerDoc.exists()) {
          const data = sellerDoc.data();
          setSellerStoreOnline(data.storeOnline !== undefined ? data.storeOnline : true);
        } else {
          setSellerStoreOnline(true);
        }
      } catch {
        // If we can't check, default to showing the product
        setSellerStoreOnline(true);
      } finally {
        setSellerCheckLoading(false);
      }
    };
    checkSellerStore();
  }, [product?.sellerId, user?.id]);
  // Handle add to cart action
  const handleAddToCart = () => {
    if (!isAuthenticated) {
      // Store pending action in localStorage for persistence across page reloads
      localStorage.setItem('pendingAddToCart', productId || '');
      navigate('/sell-with-us', {
        state: {
          openAuth: true,
          initialMode: 'signup',
          redirectTo: `/product/${productId}`
        }
      });
      return;
    }
    if (!product) return;
    setPurchaseStatus('adding');
    try {
      addItem({
        id: product.id || '',
        title: product.title,
        price: product.price,
        image: product.image || ''
      });
      setPurchaseStatus('added');
      // Open cart sidebar to show the added item
      openCart();
      // Reset status after 3 seconds
      setTimeout(() => {
        setPurchaseStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('[AddToCart] Error adding to cart:', error);
      setPurchaseStatus('error');
      // Reset status after 3 seconds
      setTimeout(() => {
        setPurchaseStatus('idle');
      }, 3000);
    }
  };
  // Check for pending add to cart action after authentication
  useEffect(() => {
    if (isAuthenticated && product) {
      const pendingProductId = localStorage.getItem('pendingAddToCart');
      if (pendingProductId === productId) {
        // Clear the pending action
        localStorage.removeItem('pendingAddToCart');
        // Execute the add to cart action
        setPurchaseStatus('adding');
        try {
          addItem({
            id: product.id || '',
            title: product.title,
            price: product.price,
            image: product.image || ''
          });
          setPurchaseStatus('added');
          // Open cart sidebar to show the added item
          openCart();
          // Reset status after 3 seconds
          setTimeout(() => {
            setPurchaseStatus('idle');
          }, 3000);
        } catch (error) {
          console.error('[AddToCart] Error adding to cart:', error);
          setPurchaseStatus('error');
          // Reset status after 3 seconds
          setTimeout(() => {
            setPurchaseStatus('idle');
          }, 3000);
        }
      }
    }
  }, [isAuthenticated, product, productId, addItem, openCart]);
  // Toggle wishlist/favorites status
  const toggleWishlist = () => {
    if (productId) {
      toggleFavorite(productId);
    }
  };
  const isInWishlist = productId ? isFavorited(productId) : false;
  // Share the product
  const shareProduct = () => {
    setShowShareOptions(!showShareOptions);
    setLinkCopied(false);
  };
  // Handle copy link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => {
        setLinkCopied(false);
        setShowShareOptions(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };
  // Determine if product is a video
  const isVideoProduct = product?.category === 'Short-form Video';
  const isVerticalVideo = product?.videoMetadata?.isVertical || false;
  // Detect if the logged-in user is the product owner
  const isOwner = !!(user?.id && product?.sellerId && user.id === product.sellerId);
  // Loading state
  if (loading) {
    return <>
        <Navbar scrolled={true} data-id="element-4854" />
        <div className="container mx-auto px-4 py-24 flex justify-center items-center min-h-screen" data-id="element-4855">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489]" data-id="element-4856"></div>
        </div>
        <Footer data-id="element-4857" />
      </>;
  }
  // Error state
  if (error || !product) {
    return <>
        <Navbar scrolled={true} data-id="element-4858" />
        <div className="container mx-auto px-4 py-24 flex flex-col justify-center items-center min-h-screen" data-id="element-4859">
          <div className="bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-4 rounded-lg mb-4" data-id="element-4860">
            <p data-id="element-4861">Error loading product. Please try again later.</p>
            {error && <p className="text-sm mt-2" data-id="element-4862">{error.message}</p>}
          </div>
          <button onClick={() => navigate('/browse')} className="mt-4 bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-all" data-id="element-4863">
            Browse Products
          </button>
        </div>
        <Footer data-id="element-4864" />
      </>;
  }
  // Store offline: block non-owners
  if (!sellerCheckLoading && sellerStoreOnline === false && !isOwner) {
    return <>
        <Navbar scrolled={true} data-id="element-4865" />
        <div className="container mx-auto px-4 py-24 flex flex-col justify-center items-center min-h-screen" data-id="element-4866">
          <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center max-w-md" data-id="element-4867">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 dark:bg-[#1A3330] flex items-center justify-center" data-id="element-4868">
              <InfoIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-4869" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-4870">
              This product is currently unavailable
            </h1>
            <p className="text-gray-600 dark:text-[#B0C1BD] mb-8" data-id="element-4871">
              The seller has temporarily taken their store offline. Please check
              back later.
            </p>
            <button onClick={() => navigate('/browse')} className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-all" data-id="element-4872">
              Browse Products
            </button>
          </div>
        </div>
        <Footer data-id="element-4873" />
      </>;
  }
  // Show loading while checking seller store status
  if (sellerCheckLoading) {
    return <>
        <Navbar scrolled={true} data-id="element-4874" />
        <div className="container mx-auto px-4 py-24 flex justify-center items-center min-h-screen" data-id="element-4875">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489]" data-id="element-4876"></div>
        </div>
        <Footer data-id="element-4877" />
      </>;
  }
  // Format the product description for display
  const formatDescription = (description: string) => {
    if (!description) return '';
    // If showing full description or description is short, return it as is
    if (showFullDescription || description.length < 300) {
      return description;
    }
    // Otherwise truncate it
    return description.substring(0, 300) + '...';
  };
  return <>
      <Navbar scrolled={true} data-id="element-4878" />
      <main className="container mx-auto px-4 pt-24 pb-12 md:pt-28 md:pb-16 max-w-7xl" data-id="element-4879">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-4880">
          {/* Left Column - Product Media */}
          <div className={`space-y-4 w-full ${isVideoProduct ? 'max-w-sm' : 'max-w-lg'} mx-auto lg:mx-0 flex items-center`} data-id="element-4881">
            {isVideoProduct /* Video Player for video products */ ? <div className="rounded-xl overflow-hidden w-full" data-id="element-4882">
                <div className="relative mx-auto" style={{
              width: '100%',
              maxWidth: '320px',
              aspectRatio: '9 / 16'
            }} data-id="element-4883">
                  <style data-id="element-4884">
                    {`.product-detail-video-container .vertical-video {
                      max-height: none !important;
                      height: 100% !important;
                      width: 100% !important;
                      object-fit: cover !important;
                    }
                    .product-detail-video-container .vertical-video-container {
                      height: 100% !important;
                      aspect-ratio: 9 / 16 !important;
                    }
                    .product-detail-video-container video {
                      object-fit: cover !important;
                    }`}
                  </style>
                  <div className="product-detail-video-container absolute inset-0" data-id="element-4885">
                    <ResponsiveVideoPlayer videoUrl={product.videoUrl || ''} title={product.title} posterImage={product.image} allowDownload={false} autoPlay={false} videoMetadata={product.videoMetadata} data-id="element-4886" />
                  </div>
                </div>
              </div> /* Image Gallery for regular products */ : <>
                <div className="bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm" data-id="element-4887">
                  <div className="relative aspect-[4/3] overflow-hidden bg-black" data-id="element-4888">
                    <img src={selectedImage} alt={product.title} className="w-full h-full object-cover" data-id="element-4889" />
                  </div>
                </div>
                {/* Thumbnail gallery */}
                {product.previewImages && product.previewImages.length > 0 && <div className="flex space-x-3 overflow-x-auto pb-2" data-id="element-4890">
                    <div className={`w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 ${selectedImage === product.image ? 'border-[#2DB489] dark:border-[#4ED1A0]' : 'border-transparent'}`} onClick={() => setSelectedImage(product.image || '')} data-id="element-4891">
                      <img src={product.image} alt={`${product.title} - main`} className="w-full h-full object-cover" data-id="element-4892" />
                    </div>
                    {product.previewImages.map((img, index) => <div key={index} className={`w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 ${selectedImage === img ? 'border-[#2DB489] dark:border-[#4ED1A0]' : 'border-transparent'}`} onClick={() => setSelectedImage(img)} data-id="element-4893">
                        <img src={img} alt={`${product.title} - preview ${index + 1}`} className="w-full h-full object-cover" data-id="element-4894" />
                      </div>)}
                  </div>}
              </>}
          </div>

          {/* Right Column - Product Info */}
          <div className="space-y-6" data-id="element-4895">
            {/* Product category */}
            <div data-id="element-4896">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400" data-id="element-4897">
                <TagIcon className="w-3 h-3 mr-1" data-id="element-4898" />
                {product.category}
              </span>
            </div>

            {/* Product title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-4899">
              {product.title}
            </h1>

            {/* Seller info and date */}
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4900">
              <div className="flex items-center" data-id="element-4901">
                <UserIcon className="w-4 h-4 mr-1" data-id="element-4902" />
                <span data-id="element-4903">By {product.sellerName || 'Unknown Seller'}</span>
              </div>
              <div className="flex items-center" data-id="element-4904">
                <CalendarIcon className="w-4 h-4 mr-1" data-id="element-4905" />
                <span data-id="element-4906">
                  Added{' '}
                  {new Date(product.createdAt || Date.now()).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline" data-id="element-4907">
              <span className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-4908">
                ${product.price}
              </span>
              {product.originalPrice && product.originalPrice > product.price && <span className="ml-2 text-lg text-gray-500 dark:text-[#7A8F8A] line-through" data-id="element-4909">
                    ${product.originalPrice}
                  </span>}
            </div>

            {/* Description */}
            <div data-id="element-4910">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4911">
                Description
              </h2>
              <p className="text-gray-600 dark:text-[#B0C1BD] whitespace-pre-line" data-id="element-4912">
                {formatDescription(product.description)}
              </p>
              {product.description && product.description.length > 300 && <button onClick={() => setShowFullDescription(!showFullDescription)} className="mt-2 text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium hover:underline" data-id="element-4913">
                  {showFullDescription ? 'Show less' : 'Read more'}
                </button>}
            </div>

            {/* Features */}
            {isVideoProduct ? <div data-id="element-4914">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4915">
                  Video Details
                </h2>
                <ul className="space-y-2" data-id="element-4916">
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-4917">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-4918" />
                    <span data-id="element-4919">Video Type: {product.videoType || 'Standard'}</span>
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-4920">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-4921" />
                    <span data-id="element-4922">Commercial Use License Included</span>
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-4923">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-4924" />
                    <span data-id="element-4925">Immediate Download After Purchase</span>
                  </li>
                </ul>
              </div> : <div data-id="element-4926">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4927">
                  Features
                </h2>
                <ul className="space-y-2" data-id="element-4928">
                  {product.format && <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-4929">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-4930" />
                      <span data-id="element-4931">Format: {product.format}</span>
                    </li>}
                  {product.licenseType && <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-4932">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-4933" />
                      <span data-id="element-4934">License: {product.licenseType}</span>
                    </li>}
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-4935">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-4936" />
                    <span data-id="element-4937">Immediate Download</span>
                  </li>
                </ul>
              </div>}

            {/* Action buttons */}
            {isOwner ? <div className="pt-4 space-y-3" data-id="element-4938">
                {/* Owner quick actions */}
                <div className="flex space-x-3" data-id="element-4939">
                  <button onClick={() => navigate('/seller-dashboard')} className="flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-[#B0C1BD] bg-gray-100 dark:bg-[#1A3330] hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors" data-id="element-4940">
                    <LayoutDashboardIcon className="w-4 h-4 mr-2" data-id="element-4941" />
                    Back to Dashboard
                  </button>
                  <button onClick={() => navigate('/seller-dashboard')} className="flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-[#2DB489] dark:bg-[#4ED1A0] hover:opacity-90 transition-opacity" data-id="element-4942">
                    <PencilIcon className="w-4 h-4 mr-2" data-id="element-4943" />
                    Edit Product
                  </button>
                </div>
              </div> : <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4" data-id="element-4944">
                <motion.button onClick={handleAddToCart} className={`flex-1 flex items-center justify-center px-6 py-3 rounded-lg font-medium text-white ${purchaseStatus === 'adding' ? 'bg-gray-400 dark:bg-gray-600' : purchaseStatus === 'added' ? 'bg-green-500 dark:bg-green-600' : 'bg-[#0A3C52] dark:bg-[#0B3C45] hover:bg-opacity-90'}`} disabled={purchaseStatus === 'adding' || purchaseStatus === 'added'} whileHover={purchaseStatus === 'idle' ? {
              scale: 1.02
            } : {}} whileTap={purchaseStatus === 'idle' ? {
              scale: 0.98
            } : {}} data-id="element-4945">
                  {purchaseStatus === 'adding' ? <span className="flex items-center" data-id="element-4946">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-4947">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4948"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4949"></path>
                      </svg>
                      Adding...
                    </span> : purchaseStatus === 'added' ? <span className="flex items-center" data-id="element-4950">
                      <CheckIcon className="w-4 h-4 mr-2" data-id="element-4951" />
                      Added to Cart
                    </span> : <span className="flex items-center" data-id="element-4952">
                      <ShoppingCartIcon className="w-4 h-4 mr-2" data-id="element-4953" />
                      Add to Cart
                    </span>}
                </motion.button>
              </div>}

            {/* Secondary actions */}
            <div className="flex space-x-4 pt-2" data-id="element-4954">
              <button onClick={toggleWishlist} className={`flex items-center text-sm ${isInWishlist ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-[#7A8F8A] hover:text-red-500 dark:hover:text-red-400'}`} data-id="element-4955">
                <HeartIcon className={`w-4 h-4 mr-1 ${isInWishlist ? 'fill-current' : ''}`} data-id="element-4956" />
                {isInWishlist ? 'Saved' : 'Save'}
              </button>
              <div className="relative" ref={shareMenuRef} data-id="element-4957">
                <button onClick={shareProduct} className="flex items-center text-sm text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] cursor-pointer" data-id="element-4958">
                  <ShareIcon className="w-4 h-4 mr-1" data-id="element-4959" />
                  Share
                </button>
                {showShareOptions && <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} transition={{
                duration: 0.15
              }} className="absolute left-0 mt-2 w-48 bg-white dark:bg-[#162825] rounded-lg shadow-lg py-2 z-20 border border-gray-100 dark:border-[#1A3330]" data-id="element-4960">
                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-100 dark:hover:bg-[#1A3330] cursor-pointer transition-colors flex items-center justify-between" onClick={handleCopyLink} data-id="element-4961">
                      <span data-id="element-4962">{linkCopied ? 'Copied!' : 'Copy Link'}</span>
                      {linkCopied && <CheckIcon className="w-4 h-4 text-green-500 dark:text-green-400" data-id="element-4963" />}
                    </button>
                  </motion.div>}
              </div>
              {product.demoUrl && <a href={product.demoUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0]" data-id="element-4964">
                  <EyeIcon className="w-4 h-4 mr-1" data-id="element-4965" />
                  Live Preview
                </a>}
            </div>
          </div>
        </div>

        {/* Additional sections like reviews, related products, etc. would go here */}
      </main>
      <Footer data-id="element-4966" />
    </>;
};